<template>
  <div class="ja-business-metrics">
    <div class="ja-business-metrics__header">
      <h3 class="ja-business-metrics__title">Business Metrics</h3>
      <p class="ja-business-metrics__subtitle">Operational and upsell performance</p>
    </div>
    <div class="ja-business-metrics__grid">
      <div
        v-for="metric in metrics"
        :key="metric.title"
        class="ja-business-metrics__card"
        :class="{ 'ja-business-metrics__card--conversion': metric.progress !== undefined }"
      >
        <template v-if="metric.progress !== undefined">
          <div class="ja-business-metrics__conversion-content">
            <span class="ja-business-metrics__card-title">{{ metric.title }}</span>
            <span class="ja-business-metrics__conversion-value">{{ metric.value }}</span>
            <span class="ja-business-metrics__card-sub">{{ metric.subtext }}</span>
          </div>
          <div class="ja-business-metrics__ring">
            <ja-apex-chart
              type="radialBar"
              :height="96"
              width="96"
              :options="ringOptions(metric.progress)"
              :series="[metric.progress]"
            />
          </div>
        </template>

        <template v-else>
          <div class="ja-business-metrics__card-header">
            <div class="ja-business-metrics__card-icon">
              <feather-icon
                :icon="metric.icon"
                svgClasses="ja-business-metrics__card-icon-svg"
              />
            </div>
            <span class="ja-business-metrics__card-title">{{ metric.title }}</span>
          </div>
          <div class="ja-business-metrics__card-metrics">
            <span class="ja-business-metrics__card-value">{{ metric.value }}</span>
            <span class="ja-business-metrics__card-sub">{{ metric.subtext }}</span>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import JaApexChart from "./JaApexChart.vue";

export default {
  name: "BusinessMetrics",
  components: { JaApexChart },
  props: {
    metrics: { type: Array, required: true },
  },
  methods: {
    ringOptions(progress) {
      return {
        chart: { sparkline: { enabled: true } },
        plotOptions: {
          radialBar: {
            hollow: { size: "68%" },
            track: { background: "#e0e7ff" },
            dataLabels: {
              name: { show: false },
              value: {
                show: true,
                fontSize: "15px",
                fontWeight: 700,
                color: "#6366f1",
                offsetY: 4,
                formatter: () => `${progress}%`,
              },
            },
          },
        },
        colors: ["#6366F1"],
        stroke: { lineCap: "round" },
      };
    },
  },
};
</script>

<style scoped lang="scss">
.ja-business-metrics {
  background: #fff;
  border-radius: 18px;
  padding: 1.35rem 1.5rem 1.25rem;
  box-shadow: 0 1px 2px 0 rgba(20, 21, 34, 0.04);
  border: 1px solid #ececf2;

  &__header {
    margin-bottom: 1rem;
  }

  &__title {
    font-size: 1.05rem;
    font-weight: 700;
    color: #2d2d2d;
    margin: 0 0 0.3rem;
    line-height: 1.3;
  }

  &__subtitle {
    font-size: 0.8125rem;
    color: #a8a8b3;
    margin: 0;
    line-height: 1.4;
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 1rem;

    @media (max-width: 992px) {
      grid-template-columns: 1fr;
    }
  }

  &__card {
    display: flex;
    flex-direction: column;
    gap: 1.15rem;
    padding: 1.5rem;
    border-radius: 20px;
    background: #f9f9fc;
    border: 1px solid #ececf2;
    min-height: 148px;
    box-sizing: border-box;

    &--conversion {
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
      gap: 1.25rem;
      background: #f5f3ff;
      border-color: #e0e7ff;
      min-height: 132px;
    }
  }

  &__card-header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  &__card-icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: rgba(99, 102, 241, 0.12);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  &__card-icon-svg {
    width: 20px;
    height: 20px;
    color: #6366f1;
  }

  &__card-title {
    font-size: 0.875rem;
    font-weight: 500;
    color: #6b7280;
    line-height: 1.3;
  }

  &__card-metrics {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    min-width: 0;
  }

  &__card-value {
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 30px;
    font-weight: 800;
    font-style: normal;
    line-height: 100%;
    letter-spacing: -0.6px;
    color: #16172b;
  }

  &__conversion-content {
    display: flex;
    flex-direction: column;
    gap: 0.45rem;
    min-width: 0;
    flex: 1;
  }

  &__conversion-value {
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 30px;
    font-weight: 800;
    font-style: normal;
    line-height: 100%;
    letter-spacing: -0.6px;
    color: #6366f1;
  }

  &__card-sub {
    font-size: 0.8125rem;
    font-weight: 400;
    color: #9ca3af;
    line-height: 1.35;
  }

  &__ring {
    flex-shrink: 0;
  }
}
</style>
